import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { blockedUserCount, tenantsListPath, tenantsOf, TIER_UNSET, type McpTenantList } from "../../api/mcp";
import { LoadMore } from "../../components/LoadMore";
import { ResourceView } from "../../components/states";
import { Badge, Count, Empty, FilterBar, Notice, Page, Panel, SearchField, SelectField, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { labeebPage, usePagedList } from "../../hooks/usePagedList";
import { useResource } from "../../hooks/useResource";
import { ProductScope } from "../../products/PerProduct";
import {
  LABEEB_TENANTS,
  labeebPaged,
  type LabeebTenantSummary,
} from "../../api/labeeb";
import {
  labeebSuspended,
  mergeTenants,
  nameOf,
  searchTextOf,
  type MergedTenant,
} from "./mergeTenants";

export function Tenants({ config }: { config: AppConfig }) {
  const [q, setQ] = useState("");
  const [lookup, setLookup] = useState("");
  // "" = every tier, TIER_UNSET = merchants with no entitlements row.
  // Named tiers go on the wire as ?tier=. TIER_UNSET cannot: the server matches
  // the stored string exactly, and a tier-less merchant stores null.
  const [tier, setTier] = useState("");
  const { state, reload } = useResource<McpTenantList>(config, tenantsListPath(tier), [tier]);

  // Labeeb's side, fetched and MERGED — not merely joined. Its rows are
  // merchants in their own right: a merchant with a Labeeb config item and no
  // MCP history used to be absent from this table entirely. The list is a
  // page; merging a cursor that still has a next page as if it were the
  // fleet hides every business past it.
  const labeebList = usePagedList<LabeebTenantSummary>(
    config,
    labeebPaged(LABEEB_TENANTS),
    labeebPage,
  );
  const labeebRows: LabeebTenantSummary[] =
    labeebList.state.kind === "ready" ? labeebList.state.data.rows : [];
  const labeebPartial =
    labeebList.state.kind === "ready" && labeebList.state.data.next !== null;

  const navigate = useNavigate();

  return (
    <Page
      title="All tenants"
      subtitle="Every merchant this console can see, and what each product stores for them."
    >
      {/* WHOSE LIST THIS IS. Both products' lists, merged by reference: a
          merchant only Labeeb knows has its own row, badged "Labeeb only".
          Labeeb's list is a cursor page. While a next cursor remains, the
          line says the Labeeb half is partial rather than implying the
          table is the whole fleet. */}
      <ProductScope owns={["mcp", "labeeb"]}>
        {labeebPartial
          ? "MCP's verification ledger merged with Labeeb's tenants. Labeeb's list is partial — more businesses remain on the server."
          : "MCP's verification ledger merged with Labeeb's tenants"}
      </ProductScope>

      {labeebList.state.kind !== "ready" && (
        <ResourceView
          state={labeebList.state}
          what="Labeeb's tenants"
          onRetry={labeebList.reload}
        />
      )}

      {/* The list only holds merchants seen in the last year, so a dormant one
          is absent and otherwise unreachable — yet it can still be blocked.
          Without this field the 404 state would be decorative. */}
      <Panel className="p-4">
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Open any merchant by reference, including one not listed below:
        </p>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const v = lookup.trim();
            if (v) navigate(`/tenants/${encodeURIComponent(v)}`);
          }}
        >
          {/* The width goes on a WRAPPER. F7's Input renders inside its own
              `w-full` span, so `className="w-56"` on the Input was overridden
              and the field filled the panel — pushing Open onto its own line
              and making a one-field lookup two rows tall. */}
          <div className="w-64">
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="Business reference, e.g. 507077"
              aria-label="Business reference"
            />
          </div>
          <Button type="submit" disabled={!lookup.trim()}>
            Open
          </Button>
        </form>
      </Panel>

      <ResourceView state={state} what="the merchant list" onRetry={reload} />

      {/* ONE PRODUCT DOWN MUST NOT ERASE THE OTHER'S ROWS.
          This table is a MERGE of two independent products, so gating it on
          MCP alone meant a Labeeb-only merchant — the very thing the merge
          exists to surface — vanished behind a message about MCP. Render
          whenever EITHER side has rows; the failure above stays on screen, so
          partial data is never dressed up as complete. */}
      {(state.kind === "ready" || labeebRows.length > 0) &&
        (() => {
          const mcp = state.kind === "ready" ? state.data : null;
          const all: MergedTenant[] = mergeTenants(mcp ? tenantsOf(mcp) : [], labeebRows);
          const everywhere = mcp?.users_blocked_across_all_merchants ?? [];
          // Both filters read MCP's tier: the ?tier= parameter is MCP's, so a
          // Labeeb-only merchant has no MCP tier to match and drops out of a
          // filtered view. That is correct — it is not on that MCP tier — and
          // it is why the unfiltered list is the one that shows everything.
          //
          // The server narrows MCP's half by ?tier=, but Labeeb's rows are
          // merged in AFTER that, so a named tier is filtered here too.
          // Trusting the server alone listed every Labeeb-only merchant under
          // "plus" (DA-3579). TIER_UNSET is the one slice ?tier= cannot
          // express at all.
          const byTier =
            tier === TIER_UNSET
              ? all.filter((r) => r.mcp?.tier == null)
              : tier
                ? all.filter((r) => r.mcp?.tier === tier)
                : all;
          const rows = q
            ? byTier.filter((r) => searchTextOf(r).includes(q.toLowerCase()))
            : byTier;

          return (
            <>
              <FilterBar>
                <SearchField
                  value={q}
                  onChange={setQ}
                  placeholder="Reference, name, email, or a Labeeb/entitlements id…"
                  label="Filter merchants"
                />
                {/* "MCP tier", as the column says: Labeeb's list row carries no tier,
                    so its tiers cannot be offered here (DA-3578). */}
                <SelectField value={tier} onChange={setTier} label="Filter by MCP tier" width={150}>
                  <option value="">Every tier</option>
                  {/* MCP's tiers. Empty when MCP is down, which leaves the
                      filter at "Every tier" rather than offering slices that
                      cannot be applied. */}
                  {(mcp?.known_tiers ?? []).map((t: string) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value={TIER_UNSET}>not set</option>
                </SelectField>
                <Count shown={rows.length} total={all.length} />
              </FilterBar>

              {rows.length === 0 && (
                <Empty>
                  {q
                    ? "Nothing matches that filter."
                    : tier
                      ? "No merchants on that tier."
                      : "No merchants have connected yet."}
                </Empty>
              )}

              {/* NOT dense. Every row here carries two lines in three of its
                  cells plus an activity cell — the one table in the design
                  left at full height, because dense squeezes the second line
                  into the first and the reference under the name stops being
                  readable. */}
              {rows.length > 0 && (
              <Table
                dense={false}
                head={
                  <tr>
                    <Th>Reference</Th>
                    <Th>Name</Th>
                    {/* MCP's tier only. Labeeb's list row (`TenantOut`)
                        carries no tier — it is a per-business document,
                        and reading one per row would be N requests. Both
                        tiers sit side by side on the merchant page. */}
                    <Th>MCP tier</Th>
                    <Th>Status</Th>
                  </tr>
                }
                    >
                {rows.map((m) => (
                  // Ops know the numeric reference; the UUID is internal and
                  // never surfaced — it is not even in the type.
                  <tr key={m.reference} className="hover:bg-slate-950/[2.5%] dark:hover:bg-white/5">
                    <Td>
                      <Link
                        to={`/tenants/${encodeURIComponent(m.reference)}`}
                        className="ref"
                      >
                        {m.reference}
                      </Link>
                    </Td>
                    <Td>
                      {/* Labeeb ships a name too — its list attaches
                          business_name from the BusinessProfile item. The
                          console used to declare neither field and render
                          every Labeeb-only merchant as a bare em dash. */}
                      <span className="flex flex-wrap items-center gap-1.5">
                        {nameOf(m) ?? (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                        {!m.mcp && <Badge tone="neutral">Labeeb only</Badge>}
                        {/* "Configured, never used" is gone: it detected a
                            config row with no profile, which was the shape of
                            an accidental enrolment. The new API has no such
                            row to create — a quota write 404s on a business
                            Labeeb does not know — so the badge had nothing
                            left to warn about.
                            Suspension replaces it, and is the state actually
                            worth catching while scanning a list. */}
                        {labeebSuspended(m) && <Badge tone="red">Labeeb suspended</Badge>}
                      </span>
                    </Td>
                    <Td>
                      {/* null is NOT "free": the server resolves an absent
                          row as free, but nothing has been stored, and
                          rendering "free" would claim a decision nobody made. */}
                      {!m.mcp ? (
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          not in MCP
                        </span>
                      ) : m.mcp.tier ? (
                        <span className="ref text-xs">{m.mcp.tier}</span>
                      ) : (
                        <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
                          no row
                        </span>
                      )}
                    </Td>
                    <Td>
                      {/* PER PRODUCT, and named as such: `blocked` is MCP's
                          blocklist. Labeeb's standing is the "Labeeb
                          suspended" badge beside the name, from its list row. */}
                      <div className="flex flex-wrap items-center gap-1">
                        {m.mcp ? (
                          m.mcp.blocked ? (
                            <Badge tone="red">MCP blocked</Badge>
                          ) : (
                            <Badge tone="green">MCP active</Badge>
                          )
                        ) : (
                          // MCP has never seen this merchant, so it is neither
                          // blocked nor active there — a third state, and
                          // painting it green would invent a fact.
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            not in MCP
                          </span>
                        )}
                        {/* Labeeb appears only when it knows the merchant. */}
                        {m.labeeb && <Badge tone="neutral">Labeeb</Badge>}
                      </div>
                      {m.mcp && blockedUserCount(m.mcp) > 0 && (
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                          {blockedUserCount(m.mcp)} user
                          {blockedUserCount(m.mcp) === 1 ? "" : "s"} blocked
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
              )}

              {/* NOT a property of any merchant above — these are ("*", email)
                  rows, blocked everywhere. Reported separately for that reason.

                  No `mt-4`: the column this sits in is the Page's own
                  `gap-6`, and a margin on top of it made the gap under the
                  table half again as large as every other gap on the page. */}
              {everywhere.length > 0 && (
                <Notice tone="amber" title="Blocked across all merchants">
                  <p className="text-xs">
                    These are not attached to any merchant in the list — they apply
                    everywhere.
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {everywhere.map((e) => (
                      <li key={e} className="ref text-xs">
                        {e}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs">
                    <Link to="/policy/access" className="underline">
                      Manage blocks
                    </Link>
                  </p>
                </Notice>
              )}

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {rows.length} shown
                {q && byTier.length !== rows.length ? ` of ${byTier.length}` : ""}.
                Merchants appear here from the verification ledger, whose entries expire
                after a year — a dormant merchant is absent but can still be opened by
                reference above.
              </p>

              {labeebList.state.kind === "ready" && (
                <LoadMore
                  shown={labeebRows.length}
                  noun="Labeeb businesses"
                  hasMore={labeebPartial}
                  loading={labeebList.loadingMore}
                  error={labeebList.moreError}
                  onMore={labeebList.loadMore}
                />
              )}
            </>
          );
        })()}
    </Page>
  );
}
